import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonLoading,
  IonPage,
  IonToolbar,
  useIonAlert,
  useIonToast,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from "@ionic/react";
import "./WorkOrders.css";
import { useQueryExec } from "../hooks/useQueryExec";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";
import PageHeader from "../components/PageHeader";
import WorkOrderResults from "../components/WorkOrderResults";
import QueryFilter from "../components/QueryFilter";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import SearchFooter from "../components/SearchFooter";
import EmployeeSearch from "../components/EmployeeSearch";
import { getEmployeesLiteList } from "../api/Employees";
import { Employee } from "../models/employees/Employees";
import { massUpdateOfWOTechnician } from "../api/WorkOrders";
import { getUser } from "../util/UserHelper";
import { useParams } from "react-router-dom";
import useDownloadWorkOrdersForOfflineAccess from "../hooks/useDownloadWorkOrdersForOfflineAccess";
import { useWorkOrderStore } from "../store/wo-order";
import { useOfflineStore } from "../store/offline-mode";

const WorkOrders: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);

  const [isEmployeeSearchOpened, setIsEmployeeSearchOpened] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const { forOfflineAccess } = useParams<{ forOfflineAccess: string }>();
  const isOffline = useOfflineStore((state) => state.isOffline);
  const isForOfflineAccess =
    isOffline || forOfflineAccess?.toLowerCase() === "true";
  const [isSelectable, setIsSelectable] = useState(isForOfflineAccess);
  const [presentAlert] = useIonAlert();
  const alertShown = useRef(false);

  const {
    getQueryList,
    queryList,
    getFilterParams,
    filterParams,
    setParameter,
    queryRows,
    executeSearch,
    getPriorityCodes,
    getStatusCodes,
    errorState,
    totalCount,
    queryCriteria,
    getQueryCriteria,
    getNoOfFieldsToDisplay,
    showLoading,
    setShowLoading,
    searchYn,
    setSearchYn,
    queryName,
    criteriaParams,
    confirmSearch,
    execFilter,
    resultsFilter,
    filterRows,
    queryInfo,
  } = useQueryExec("Work Order", "Work Order", "cf_work_order");

  useIonViewDidEnter(() => {
    if (!isForOfflineAccess) return;

    if (!alertShown.current) {
      alertShown.current = true;
      presentAlert({
        message:
          translatedMessages["MobWoForOfflineAccess"]?.MessageText ||
          "Before losing connection, search and download the work orders needed in offline mode.  All downloaded work orders will be displayed on your home screen.",
        buttons: ["OK"],
      });
    }
    setIsSelectable(true);
  }, [isForOfflineAccess]);

  useIonViewDidLeave(() => {
    setIsSelectable(false); //reset it
  });

  useEffect(() => {
    if (!isOffline) {
      alertShown.current = false;
      setIsSelectable(false);
    }
  }, [isOffline]);

  const querySection = "QW--Work Order";
  const [presentToast] = useIonToast();
  const { trigger } = useDownloadWorkOrdersForOfflineAccess();
  const selectedWO = useWorkOrderStore((state) => state.selectedWo);
  const toggleWo = useWorkOrderStore((state) => state.toggleWo);
  const setDownloadedWo = useWorkOrderStore((state) => state.setDownloadedWo);
  const resetSelectedWo = useWorkOrderStore((state) => state.resetSelectedWo);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (showLoading) resetSelectedWo(); //reset the selected work order if user searches again
  }, [resetSelectedWo, showLoading]);

  const getEmployees = useCallback(async () => {
    setShowLoader(true);
    const data = {
      PropertyList: ["EmployeeName", "EmployeeId"],
      Filter: "",
    };
    const response = await getEmployeesLiteList(data);
    setEmployees(response.data as Employee[]);
    setShowLoader(false);
  }, []);

  const handleSave = useCallback(async () => {
    await getEmployees();
    setIsEmployeeSearchOpened(true);
  }, [getEmployees]);

  const handleDownloadWoForOfflineAccess = useCallback(async () => {
    setShowLoader(true);
    await trigger(selectedWO.map((s) => s.id));
    setDownloadedWo(selectedWO);
    resetSelectedWo();
    setShowLoader(false);
    presentAlert({
      message:
        translatedMessages["MobOfflineWoDownloadComplete"]?.MessageText ||
        "Work orders downloaded for offline access.",
      buttons: ["OK"],
    });
  }, [
    presentAlert,
    resetSelectedWo,
    selectedWO,
    setDownloadedWo,
    translatedMessages,
    trigger,
  ]);

  const handleWoAssignment = useCallback(
    async (value?: Employee) => {
      const employeeId = value?.Properties?.EmployeeId ?? "";
      setIsEmployeeSearchOpened(false);
      if (employeeId) {
        try {
          setShowLoading(true);
          const response = await massUpdateOfWOTechnician({
            ChangedPropertyNames: ["AssignedTo"],
            ListOfWO: selectedWO.map((w) => w.id),
            ModifiedWoData: {
              AssignedTo: employeeId,
            },
            UserDefinedValues: {},
          });

          const hasError = !!(
            response.isError ||
            (response.data?.WoResultsList ?? []).some(
              (w: any) => w?.WoResult?.Success === false
            )
          );
          const message = hasError
            ? "Error assigning work orders."
            : `Work orders assigned to ${
                value?.Properties?.EmployeeName ?? ""
              }`;

          presentToast({
            message,
            duration: 3000,
            position: "top",
            color: hasError ? "danger" : "success",
          });
        } finally {
          setShowLoading(false);
        }
      }

      setIsSelectable(false);
      resetSelectedWo();
    },
    [presentToast, resetSelectedWo, selectedWO, setShowLoading]
  );

  const handleAssignToSelf = useCallback(async () => {
    setShowLoading(true);
    const user = await getUser();
    if (user) {
      await handleWoAssignment({
        Properties: {
          EmployeeId: user.Id,
          EmployeeName: user.DisplayName,
        },
        EmployeeId: user.Id,
      });
    }
    setShowLoading(false);
  }, [handleWoAssignment, setShowLoading]);

  return (
    <IonPage>
      <PageHeader title={translations["lbl_WorkOrders"] || "Work Orders"}>
        {totalCount > 0 && !isForOfflineAccess && (
          <IonButton
            onClick={() => setIsSelectable(true)}
            fill="clear"
            slot="end"
            disabled={isSelectable}
          >
            {translations["mob_lbl_assign_tech"] || "Assign Tech"}
          </IonButton>
        )}
      </PageHeader>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
      />
      <IonLoading
        isOpen={showLoader}
        onDidDismiss={() => setShowLoader(false)}
      />

      <IonContent>
        <QueryFilter
          setShowLoading={setShowLoading}
          getQueryList={getQueryList}
          queryList={queryList}
          querySection={querySection}
          getFilterParams={getFilterParams}
          filterParams={filterParams}
          setParameter={setParameter}
          queryCriteria={queryCriteria}
          getQueryCriteria={getQueryCriteria}
          searchCallback={confirmSearch}
          queryInfo={queryInfo}
          triggerId="work-order-popover-trigger"
        />
        <WorkOrderResults
          searchYn={searchYn}
          setSearchYn={setSearchYn}
          queryName={queryName}
          queryParams={filterParams}
          criteria={criteriaParams}
          queryRows={queryRows}
          setParameter={setParameter}
          executeSearch={executeSearch}
          getPriorityCodes={getPriorityCodes}
          getStatusCodes={getStatusCodes}
          getNoOfFieldsToDisplay={getNoOfFieldsToDisplay}
          errorState={errorState}
          totalCount={totalCount}
          showLoading={showLoading}
          setShowLoading={setShowLoading}
          filterRows={filterRows}
          filterMethod={execFilter}
          filterString={resultsFilter}
          isSelectable={isSelectable}
          onToggle={(data) => toggleWo(data)}
          selectedWoIds={selectedWO.map((w) => w.id)}
        />
        <EmployeeSearch
          isOpen={isEmployeeSearchOpened}
          employees={employees}
          onCancel={() => setIsEmployeeSearchOpened(false)}
          onSelect={handleWoAssignment}
        />
      </IonContent>
      <SearchFooter totalRows={totalCount} filterRows={filterRows}>
        {isSelectable && (
          <IonToolbar>
            {!isForOfflineAccess ? (
              <>
                <IonButtons slot="start">
                  <IonButton
                    onClick={() => setIsSelectable(false)}
                    fill="solid"
                  >
                    {translations["lbl_btn_cancel"] || "Cancel"}
                  </IonButton>
                </IonButtons>
                <IonButtons slot="end">
                  <IonButton
                    disabled={selectedWO.length <= 0}
                    onClick={handleAssignToSelf}
                    fill="solid"
                  >
                    {translations["mob_lbl_assign_to_myself"] ||
                      "Assign To Myself"}
                  </IonButton>
                  <IonButton
                    disabled={selectedWO.length <= 0}
                    onClick={handleSave}
                    fill="solid"
                  >
                    {translations["mob_lbl_assign_to"] || "Assign To"}
                  </IonButton>
                </IonButtons>
              </>
            ) : (
              <IonButtons slot="end">
                <IonButton
                  disabled={selectedWO.length <= 0}
                  onClick={handleDownloadWoForOfflineAccess}
                  fill="solid"
                >
                  {translations["mob_lbl_download"] || "Download"}
                </IonButton>
              </IonButtons>
            )}
          </IonToolbar>
        )}
      </SearchFooter>
    </IonPage>
  );
};

export default withAITracking(reactPlugin, WorkOrders, "WorkOrders");
